import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useU2000WebSocketContext } from '../../u2000Core/context/websocket/U2000WebSocketContext';
import {
  isU3204LlmConversationTitleMessage,
  isU3204LlmDoneMessage,
  isU3204LlmErrorMessage,
  isU3204LlmExportReadyMessage,
  isU3204LlmThinkingMessage,
  isU3204LlmTokenMessage,
  isU3204LlmToolDoneMessage,
  isU3204LlmToolStartMessage,
  U3204_LLM_CONVERSATION_TITLE_WS_EVENT,
  U3204_LLM_DONE_WS_EVENT,
  U3204_LLM_ERROR_WS_EVENT,
  U3204_LLM_EXPORT_READY_WS_EVENT,
  U3204_LLM_THINKING_WS_EVENT,
  U3204_LLM_TOKEN_WS_EVENT,
  U3204_LLM_TOOL_DONE_WS_EVENT,
  U3204_LLM_TOOL_START_WS_EVENT,
} from '../../u2000Core/types/U2000WebSocketMessages';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../U3204';
import { useU3204Context } from '../U3204Context';
import { U3204_ASSISTANT_PLACEHOLDER_ID } from '../U3204Service';
import { u3204UpsertToolActivityItem } from '../utils/u3204MessageClientState';
import { u3204PatchConversationTitleInList } from '../utils/u3204PatchConversationTitleInList';
import { useU3204ConversationHandlers } from './useU3204ConversationHandlers';

function findAssistantPlaceholderIndex(
  messages: { id: string; role: string; generationStatus?: string }[],
  messageId: string,
): number {
  return messages.findIndex(
    (m) =>
      m.id === U3204_ASSISTANT_PLACEHOLDER_ID ||
      (m.role === 'assistant' && m.generationStatus === 'pending' && m.id === messageId),
  );
}

export function useU3204ChatStream() {
  const { on, off } = useU2000WebSocketContext();
  const { t } = useTranslation(NS);
  const toast = useToast();
  const { initLU, activeConversationId, setMessages, setStreamState, setConversations, setActiveConversationTitle } =
    useU3204Context();
  const { refreshActiveConversation, loadConversations } = useU3204ConversationHandlers();
  const activeConversationIdRef = useRef(activeConversationId);
  activeConversationIdRef.current = activeConversationId;
  const showThinkingRef = useRef(initLU.showThinking);
  showThinkingRef.current = initLU.showThinking;

  useEffect(() => {
    const subscribeThinking = initLU.showThinking;

    const handleToken = (message: unknown) => {
      if (!isU3204LlmTokenMessage(message)) return;
      if (message.conversationId !== activeConversationIdRef.current) return;

      setStreamState('streaming');
      setMessages((prev) => {
        const placeholderIndex = findAssistantPlaceholderIndex(prev, message.messageId);

        if (placeholderIndex === -1) {
          return [
            ...prev,
            {
              id: message.messageId,
              role: 'assistant' as const,
              content: message.delta,
              generationStatus: 'pending' as const,
            },
          ];
        }

        return prev.map((m, index) => {
          if (index !== placeholderIndex) return m;
          const isPlaceholder = m.id === U3204_ASSISTANT_PLACEHOLDER_ID;
          return {
            ...m,
            id: message.messageId,
            content: (isPlaceholder ? '' : m.content) + message.delta,
            generationStatus: 'pending' as const,
            isOptimistic: false,
          };
        });
      });
    };

    const handleToolStart = (message: unknown) => {
      if (!isU3204LlmToolStartMessage(message)) return;
      if (message.conversationId !== activeConversationIdRef.current) return;

      setStreamState('streaming');
      setMessages((prev) => {
        const placeholderIndex = findAssistantPlaceholderIndex(prev, message.messageId);

        if (placeholderIndex === -1) {
          return [
            ...prev,
            {
              id: message.messageId,
              role: 'assistant' as const,
              content: '',
              toolActivity: u3204UpsertToolActivityItem(undefined, message.toolName, 'running'),
              generationStatus: 'pending' as const,
            },
          ];
        }

        return prev.map((m, index) => {
          if (index !== placeholderIndex) return m;
          return {
            ...m,
            id: message.messageId,
            toolActivity: u3204UpsertToolActivityItem(m.toolActivity, message.toolName, 'running'),
            generationStatus: 'pending' as const,
            isOptimistic: false,
          };
        });
      });
    };

    const handleToolDone = (message: unknown) => {
      if (!isU3204LlmToolDoneMessage(message)) return;
      if (message.conversationId !== activeConversationIdRef.current) return;

      setStreamState('streaming');
      setMessages((prev) => {
        const placeholderIndex = findAssistantPlaceholderIndex(prev, message.messageId);

        if (placeholderIndex === -1) {
          return [
            ...prev,
            {
              id: message.messageId,
              role: 'assistant' as const,
              content: '',
              toolActivity: u3204UpsertToolActivityItem(undefined, message.toolName, message.status),
              generationStatus: 'pending' as const,
            },
          ];
        }

        return prev.map((m, index) => {
          if (index !== placeholderIndex) return m;
          return {
            ...m,
            id: message.messageId,
            toolActivity: u3204UpsertToolActivityItem(m.toolActivity, message.toolName, message.status),
            generationStatus: 'pending' as const,
            isOptimistic: false,
          };
        });
      });
    };

    const handleThinking = (message: unknown) => {
      if (!showThinkingRef.current) return;
      if (!isU3204LlmThinkingMessage(message)) return;
      if (message.conversationId !== activeConversationIdRef.current) return;

      setStreamState('streaming');
      setMessages((prev) => {
        const placeholderIndex = findAssistantPlaceholderIndex(prev, message.messageId);

        if (placeholderIndex === -1) {
          return [
            ...prev,
            {
              id: message.messageId,
              role: 'assistant' as const,
              content: '',
              thinkingContent: message.delta,
              generationStatus: 'pending' as const,
            },
          ];
        }

        return prev.map((m, index) => {
          if (index !== placeholderIndex) return m;
          const isPlaceholder = m.id === U3204_ASSISTANT_PLACEHOLDER_ID;
          const existingThinking = isPlaceholder ? '' : (m.thinkingContent ?? '');
          return {
            ...m,
            id: message.messageId,
            thinkingContent: existingThinking + message.delta,
            generationStatus: 'pending' as const,
            isOptimistic: false,
          };
        });
      });
    };

    const handleDone = (message: unknown) => {
      if (!isU3204LlmDoneMessage(message)) return;
      if (message.conversationId !== activeConversationIdRef.current) return;

      setStreamState('idle');
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== message.messageId && m.id !== U3204_ASSISTANT_PLACEHOLDER_ID) return m;
          const nextSources =
            message.sources && message.sources.length > 0 ? message.sources : undefined;
          return {
            ...m,
            id: message.messageId,
            generationStatus: 'completed' as const,
            inputTokens: message.usage?.inputTokens ?? m.inputTokens,
            outputTokens: message.usage?.outputTokens ?? m.outputTokens,
            ...(nextSources ? { sources: nextSources } : {}),
            isOptimistic: false,
          };
        }),
      );

      void refreshActiveConversation();
      void loadConversations();
    };

    const handleConversationTitle = (message: unknown) => {
      if (!isU3204LlmConversationTitleMessage(message)) return;

      setConversations((prev) =>
        u3204PatchConversationTitleInList(prev, {
          conversationId: message.conversationId,
          title: message.title,
          version: message.version,
        }),
      );

      if (message.conversationId === activeConversationIdRef.current) {
        setActiveConversationTitle(message.title);
      }
    };

    /** Excel / Word / PDF : carte via `exportFile` + mime (pas de texte « prêt » — hint sur la carte). */
    const handleExportReady = (message: unknown) => {
      if (!isU3204LlmExportReadyMessage(message)) return;
      if (message.conversationId !== activeConversationIdRef.current) return;

      const exportFile = {
        fileUrl: message.fileUrl,
        fileName: message.fileName,
        fileMimeType: message.fileMimeType,
      };

      setMessages((prev) => {
        const existingIndex = prev.findIndex((m) => m.id === message.messageId);
        if (existingIndex >= 0) {
          return prev.map((m, index) =>
            index === existingIndex
              ? {
                  ...m,
                  exportFile,
                  generationStatus: 'completed' as const,
                }
              : m,
          );
        }

        return [
          ...prev,
          {
            id: message.messageId,
            role: 'assistant' as const,
            content: '',
            generationStatus: 'completed' as const,
            exportFile,
          },
        ];
      });
    };

    const handleError = (message: unknown) => {
      if (!isU3204LlmErrorMessage(message)) return;
      if (message.conversationId !== activeConversationIdRef.current) return;

      setStreamState('error');
      toast.error(message.message || t('errorStreaming', { ns: NS }));

      setMessages((prev) => {
        const filtered = prev.filter((m) => m.id !== U3204_ASSISTANT_PLACEHOLDER_ID);
        const errorMsg = {
          id: message.messageId ?? `error-${Date.now()}`,
          role: 'assistant' as const,
          content: message.message,
          generationStatus: 'failed' as const,
          isError: true,
          errorCode: message.errorCode,
        };
        return [...filtered, errorMsg];
      });
    };

    on(U3204_LLM_TOKEN_WS_EVENT, handleToken);
    on(U3204_LLM_TOOL_START_WS_EVENT, handleToolStart);
    on(U3204_LLM_TOOL_DONE_WS_EVENT, handleToolDone);
    on(U3204_LLM_DONE_WS_EVENT, handleDone);
    on(U3204_LLM_ERROR_WS_EVENT, handleError);
    on(U3204_LLM_CONVERSATION_TITLE_WS_EVENT, handleConversationTitle);
    on(U3204_LLM_EXPORT_READY_WS_EVENT, handleExportReady);

    if (subscribeThinking) {
      on(U3204_LLM_THINKING_WS_EVENT, handleThinking);
    }

    return () => {
      off(U3204_LLM_TOKEN_WS_EVENT, handleToken);
      off(U3204_LLM_TOOL_START_WS_EVENT, handleToolStart);
      off(U3204_LLM_TOOL_DONE_WS_EVENT, handleToolDone);
      off(U3204_LLM_DONE_WS_EVENT, handleDone);
      off(U3204_LLM_ERROR_WS_EVENT, handleError);
      off(U3204_LLM_CONVERSATION_TITLE_WS_EVENT, handleConversationTitle);
      off(U3204_LLM_EXPORT_READY_WS_EVENT, handleExportReady);
      if (subscribeThinking) {
        off(U3204_LLM_THINKING_WS_EVENT, handleThinking);
      }
    };
  }, [
    initLU.showThinking,
    loadConversations,
    off,
    on,
    refreshActiveConversation,
    setActiveConversationTitle,
    setConversations,
    setMessages,
    setStreamState,
    t,
    toast,
  ]);
}
